<template>
  <div>
    <template v-if="editable">
      <v-tabs
        v-if="showLanguageTabs && $store.getters.isLocaleEnabled"
        hide-slider
        background-color="transparent"
        height="28"
        color="primary"
        class="mx-3"
      >
        <v-tab
          v-for="(item, index) in localisedValues"
          :key="`tab_${item.lang}_${index}`"
          :value="item.lang"
          class="px-2 rounded-t-lg"
          :class="selectedTab === item.lang ? 'v-tab--active' : ''"
          style="min-width: 36px; border: 1px solid; border-bottom: 0"
          @click="selectedTab = item.lang"
        >
          {{ item.lang }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="selectedTab" class="transparent">
        <v-tab-item
          v-for="(item, index) in localisedValues"
          :key="`tab_item_${item.lang}_${index}`"
          :value="item.lang"
          :transition="false"
        >
          <ContentEditable
            v-if="elementType === 'SPAN'"
            :value="item.value"
            @input="updateValues({ ...item, value: $event }, index)"
            :placeholder="placeholder"
            :editable="editable"
            :class="cssClass"
          />

          <!--line-height ui issue for text field-->
          <v-textarea
            v-else-if="elementType === 'TEXT'"
            :value="item.value"
            @input="updateValues({ ...item, value: $event }, index)"
            auto-grow
            background-color="transparent"
            flat
            full-width
            hide-details="auto"
            :outlined="textAreaOutlined"
            :dense="textAreaDense"
            :hint="placeholder"
            persistent-hint
            row-height="24"
            :rows="textAreaRows"
            class="rounded-2"
            :class="textAreaOutlined ? cssErrorClass : 'hide-underline'"
          />

          <vue-editor
            v-else-if="elementType === 'RICH'"
            :value="item.value"
            @input="updateValues({ ...item, value: $event }, index)"
            :placeholder="placeholder"
            :editor-toolbar="richEditorToolbarDefault"
            useCustomImageHandler
            @image-added="handleImageAdded"
            class="rounded-2 text-area-border d-flex flex-column-reverse"
          />
        </v-tab-item>
      </v-tabs-items>
    </template>

    <span
      v-else
      v-html="formattedLocalisedValue"
      class="text-break"
      :class="cssClass"
    />
  </div>
</template>

<script>
import ContentEditable from "@/app/core-module/components/ContentEditable";

import { uploadTrainingModuleContentImage } from "@/app/course-module/methods/uploadTrainingModuleContentImage";

export default {
  name: "LocalisedContentEditable",
  components: { ContentEditable },
  props: {
    values: Array,
    showLanguageTabs: {
      type: Boolean,
      default: false,
    },
    elementType: {
      type: String,
      default: "TEXT", //SPAN, TEXT, RICH,
    },
    textAreaRows: {
      type: Number,
      default: 1,
    },
    textAreaDense: {
      type: Boolean,
      default: true,
    },
    textAreaOutlined: {
      type: Boolean,
      default: true,
    },
    prefix: null,
    placeholder: null,
    editable: {
      type: Boolean,
      default: false,
    },
    selectedLocale: {
      type: String,
      default: "en",
    },
    cssClass: String,
    hasError: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedTab: this.selectedLocale,
      richEditorToolbarDefault: [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ["bold", "italic", "underline", "strike"], // toggled buttons
        [
          { align: "" },
          { align: "center" },
          { align: "right" },
          { align: "justify" },
        ],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
        [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
        [{ color: [] }, { background: [] }], // dropdown with defaults from theme
        ["link", "image", "video"],
        ["clean"], // remove formatting button
      ],
      richEditorToolbarFull: [
        [{ font: [] }],
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        [{ size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline", "strike"],
        [
          { align: "" },
          { align: "center" },
          { align: "right" },
          { align: "justify" },
        ],
        [{ header: 1 }, { header: 2 }],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
        [{ script: "sub" }, { script: "super" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ color: [] }, { background: [] }],
        ["link", "image", "video", "formula"],
        [{ direction: "rtl" }],
        ["clean"],
      ],
    };
  },
  computed: {
    cssErrorClass() {
      return this.hasError ? "error--text text-field-error-border" : "";
    },
    formattedLocalisedValue() {
      if (this.prefix?.length) {
        return `${this.prefix} ${this.getLocalisedValue(this.values, {
          preferredLocale: this.selectedLocale,
        })}`;
      } else {
        return `${this.getLocalisedValue(this.values, {
          preferredLocale: this.selectedLocale,
        })}`;
      }
    },
    localisedValues() {
      return this.supportedLanguages().map(lang => {
        const existing = this.values?.find(x => x.lang === lang);
        return { lang, value: existing?.value ?? "" };
      });
    },
  },
  methods: {
    updateValues(item, index) {
      this.$set(this.localisedValues, index, item);
      this.$emit("update:values", this.localisedValues);
    },
    handleImageAdded: function (file, editor, cursorLocation, resetUploader) {
      uploadTrainingModuleContentImage(file).then(response => {
        if (response.data) {
          editor.insertEmbed(
            cursorLocation,
            "image",
            this.getImage(response.data)
          );
          resetUploader();
        } else if (response.error) this.$emit("onError");
      });
    },
  },
  watch: {
    selectedLocale() {
      this.selectedTab = this.selectedLocale;
    },
  },
};
</script>

<style lang="scss" scoped>
>>> .v-textarea textarea {
  color: #281166;
}

.text-area-border {
  border: 1px solid;
}
</style>
<style scoped>
.hide-underline >>> .v-input__slot::before {
  border-style: none !important;
}
.hide-underline >>> .v-input__slot::after {
  border-style: none !important;
}
</style>
